import { pickCardiopulmonaryDetrendedSignal } from "./cardiopulmonaryView";
import { pickRr, pickRrSignalQuality } from "./signalQuality";
import { normaliseGeoRoutePoint } from "./sixMinuteWalkRouteMap";
import { readFiniteNumber } from "./viewTimeSeries";

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numericValue = readFiniteNumber(value);
    if (numericValue != null) {
      return numericValue;
    }
  }

  return null;
}

function toPeakCandidates(points = []) {
  if (!Array.isArray(points) || points.length < 3) {
    return [];
  }

  const peaks = [];

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];

    const previousValue = readFiniteNumber(previous?.value);
    const currentValue = readFiniteNumber(current?.value);
    const nextValue = readFiniteNumber(next?.value);

    if (
      previousValue == null ||
      currentValue == null ||
      nextValue == null ||
      currentValue <= previousValue ||
      currentValue < nextValue
    ) {
      continue;
    }

    peaks.push({
      index,
      t: readFiniteNumber(current?.t),
      value: currentValue,
    });
  }

  return peaks;
}

function findLowestPoint(points = [], startIndex = 0, endIndex = 0) {
  const boundedStart = Math.max(0, Math.floor(startIndex));
  const boundedEnd = Math.min(points.length - 1, Math.floor(endIndex));
  let lowestPoint = null;

  for (let index = boundedStart; index <= boundedEnd; index += 1) {
    const value = readFiniteNumber(points[index]?.value);

    if (value == null) {
      continue;
    }

    if (!lowestPoint || value < lowestPoint.value) {
      lowestPoint = {
        index,
        t: readFiniteNumber(points[index]?.t),
        value,
      };
    }
  }

  return lowestPoint;
}

function averageFiniteNumbers(values = []) {
  const finiteValues = values.filter((value) => readFiniteNumber(value) != null);

  if (!finiteValues.length) {
    return null;
  }

  return (
    finiteValues.reduce((sum, value) => sum + Number(value), 0) /
    finiteValues.length
  );
}

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length ? normalizedValue : "";
}

function buildMeasuredPeaks(points = []) {
  const peaks = toPeakCandidates(points);

  return peaks
    .map((peak, peakIndex) => {
      const priorPeak = peakIndex > 0 ? peaks[peakIndex - 1] : null;
      const trough = findLowestPoint(
        points,
        priorPeak?.index ?? 0,
        peak.index,
      );
      const height =
        trough && readFiniteNumber(peak.value) != null
          ? peak.value - trough.value
          : null;

      return {
        ...peak,
        trough,
        height: readFiniteNumber(height),
      };
    })
    .filter((peak) => readFiniteNumber(peak.height) != null && peak.height > 0);
}

function buildInspiratoryReserveSummary(points = []) {
  const peaks = buildMeasuredPeaks(points);

  if (peaks.length < 2) {
    return {
      peakCount: peaks.length,
      largestPeak: null,
      priorPeak: null,
      referencePeaks: [],
      referencePeakAverageHeight: null,
      largestPeakHeight: null,
      relativePercent: null,
      absoluteDelta: null,
      deltaRatio: null,
      peakHeightRatio: null,
    };
  }

  let bestCandidate = null;

  for (let index = 1; index < peaks.length; index += 1) {
    const largestPeak = peaks[index];
    const priorPeak = peaks[index - 1];
    const referencePeaks = peaks.slice(0, index);
    const referencePeakAverageHeight = averageFiniteNumbers(
      referencePeaks.map((peak) => peak.height),
    );

    if (
      referencePeakAverageHeight == null ||
      Math.abs(referencePeakAverageHeight) <= 1e-6
    ) {
      continue;
    }

    if (
      !bestCandidate ||
      largestPeak.height > bestCandidate.largestPeak.height
    ) {
      bestCandidate = {
        largestPeak,
        priorPeak,
        referencePeaks,
        referencePeakAverageHeight,
      };
    }
  }

  if (!bestCandidate) {
    return {
      peakCount: peaks.length,
      largestPeak: null,
      priorPeak: null,
      referencePeaks: [],
      referencePeakAverageHeight: null,
      largestPeakHeight: null,
      relativePercent: null,
      absoluteDelta: null,
      deltaRatio: null,
      peakHeightRatio: null,
    };
  }

  const absoluteDelta =
    bestCandidate.largestPeak.height - bestCandidate.referencePeakAverageHeight;
  const deltaRatio =
    Math.abs(bestCandidate.referencePeakAverageHeight) > 1e-6
      ? absoluteDelta / Math.abs(bestCandidate.referencePeakAverageHeight)
      : null;
  const peakHeightRatio =
    Math.abs(bestCandidate.referencePeakAverageHeight) > 1e-6
      ? bestCandidate.largestPeak.height /
        Math.abs(bestCandidate.referencePeakAverageHeight)
      : null;
  const relativePercent =
    readFiniteNumber(deltaRatio) != null ? deltaRatio * 100 : null;

  return {
    peakCount: peaks.length,
    largestPeak: bestCandidate.largestPeak,
    priorPeak: bestCandidate.priorPeak,
    referencePeaks: bestCandidate.referencePeaks,
    referencePeakAverageHeight: bestCandidate.referencePeakAverageHeight,
    largestPeakHeight: bestCandidate.largestPeak.height,
    relativePercent,
    absoluteDelta,
    deltaRatio,
    peakHeightRatio,
  };
}

export function pickSixMinuteWalkWorkflow(docData) {
  return docData?.workflow && typeof docData.workflow === "object"
    ? docData.workflow
    : null;
}

export function pickSixMinuteWalkStage(docData) {
  const rawStage = pickSixMinuteWalkWorkflow(docData)?.stage;

  return typeof rawStage === "string" && rawStage.trim().length
    ? rawStage.trim().toLowerCase()
    : null;
}

export function pickSixMinuteWalkAssessmentId(docData) {
  return normalizeOptionalString(pickSixMinuteWalkWorkflow(docData)?.assessmentId) || null;
}

export function pickSixMinuteWalkPairedMeasurementId(docData) {
  const workflow = pickSixMinuteWalkWorkflow(docData);

  return (
    normalizeOptionalString(workflow?.pairedMeasurementId) ||
    normalizeOptionalString(workflow?.preMeasurementId) ||
    normalizeOptionalString(workflow?.pairedRecordingId) ||
    null
  );
}

function pickSixMinuteWalkRowData(row) {
  return row?.rawMeasurement && typeof row.rawMeasurement === "object"
    ? row.rawMeasurement
    : row;
}

function isSixMinuteWalkRow(row) {
  return normalizeOptionalString(row?.variant) === "RecordingSixMinuteWalk";
}

export function collapseSixMinuteWalkHistoryRows(rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) {
    return Array.isArray(rows) ? rows : [];
  }

  const preRowsById = new Map();
  const preRowsByAssessmentId = new Map();

  rows.forEach((row) => {
    if (!isSixMinuteWalkRow(row)) {
      return;
    }

    const rowData = pickSixMinuteWalkRowData(row);
    if (pickSixMinuteWalkStage(rowData) !== "pre") {
      return;
    }

    const rowId = normalizeOptionalString(row?.id);
    if (rowId) {
      preRowsById.set(rowId, row);
    }

    const assessmentId = pickSixMinuteWalkAssessmentId(rowData);
    if (!assessmentId) {
      return;
    }

    const group = preRowsByAssessmentId.get(assessmentId) || [];
    group.push(row);
    preRowsByAssessmentId.set(assessmentId, group);
  });

  const hiddenPreRowIds = new Set();

  rows.forEach((row) => {
    if (!isSixMinuteWalkRow(row)) {
      return;
    }

    const rowData = pickSixMinuteWalkRowData(row);
    if (pickSixMinuteWalkStage(rowData) !== "post") {
      return;
    }

    const pairedMeasurementId = pickSixMinuteWalkPairedMeasurementId(rowData);
    const assessmentId = pickSixMinuteWalkAssessmentId(rowData);
    const assessmentPreRows = assessmentId
      ? preRowsByAssessmentId.get(assessmentId) || []
      : [];
    const preRow =
      (pairedMeasurementId ? preRowsById.get(pairedMeasurementId) : null) ||
      (assessmentPreRows.length === 1 ? assessmentPreRows[0] : null);

    if (preRow?.id) {
      hiddenPreRowIds.add(preRow.id);
    }
  });

  return rows.filter((row) => !hiddenPreRowIds.has(row?.id));
}

export function pickSixMinuteWalkWalkSummary(docData) {
  const walkSummary = pickSixMinuteWalkWorkflow(docData)?.walk;

  return walkSummary && typeof walkSummary === "object" ? walkSummary : null;
}

function normaliseRouteTracePoint(point, index = 0) {
  const xMeters = firstFiniteNumber(
    point?.xMeters,
    point?.x,
    point?.eastMeters,
    point?.lng,
    point?.longitude,
  );
  const yMeters = firstFiniteNumber(
    point?.yMeters,
    point?.y,
    point?.northMeters,
    point?.lat,
    point?.latitude,
  );

  if (xMeters == null || yMeters == null) {
    return null;
  }

  return {
    xMeters,
    yMeters,
    tSeconds: firstFiniteNumber(
      point?.tSeconds,
      point?.seconds,
      point?.t,
      point?.elapsedSeconds,
      index,
    ),
    totalDistanceMeters: firstFiniteNumber(
      point?.totalDistanceMeters,
      point?.distanceMeters,
      point?.distanceMetres,
    ),
  };
}

export function pickSixMinuteWalkRouteTrace(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);
  const rawTrace =
    walkSummary?.routeTrace ??
    walkSummary?.route ??
    walkSummary?.routePoints ??
    walkSummary?.path ??
    [];

  if (!Array.isArray(rawTrace)) {
    return [];
  }

  return rawTrace
    .map((point, index) => normaliseRouteTracePoint(point, index))
    .filter(Boolean);
}

export function pickSixMinuteWalkGeoRouteTrace(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);
  const rawTrace =
    walkSummary?.geoRouteTrace ??
    walkSummary?.geoRoute ??
    walkSummary?.geoPath ??
    walkSummary?.routeGeo ??
    [];

  if (!Array.isArray(rawTrace)) {
    return [];
  }

  return rawTrace
    .map((point, index) => normaliseGeoRoutePoint(point, index))
    .filter(Boolean);
}

export function pickSixMinuteWalkDistanceMeters(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);

  return firstFiniteNumber(
    walkSummary?.sixMinuteDistanceMeters,
    walkSummary?.sixMinuteDistanceMetres,
    walkSummary?.distanceMeters,
    walkSummary?.distanceMetres,
    walkSummary?.totalDistanceMeters,
  );
}

export function pickSixMinuteWalkTotalTrackedDistanceMeters(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);

  return firstFiniteNumber(
    walkSummary?.totalDistanceMeters,
    walkSummary?.totalDistanceMetres,
    walkSummary?.trackedDistanceMeters,
    walkSummary?.distanceMeters,
    walkSummary?.distanceMetres,
  );
}

export function pickSixMinuteWalkRecoveryDelayMs(docData) {
  const workflow = pickSixMinuteWalkWorkflow(docData);

  return firstFiniteNumber(
    workflow?.recoveryDelayMs,
    workflow?.postWalkDelayMs,
  );
}

export function pickSixMinuteWalkRespiratoryRate(docData) {
  return pickRr(docData);
}

export function pickSixMinuteWalkRespiratoryQuality(docData) {
  return pickRrSignalQuality(docData);
}

export function pickSixMinuteWalkDetrendedSignal(docData) {
  return pickCardiopulmonaryDetrendedSignal(docData);
}

export function pickSixMinuteWalkInspiratoryReserveSummary(docData) {
  return buildInspiratoryReserveSummary(
    pickSixMinuteWalkDetrendedSignal(docData).points,
  );
}

export function compareSixMinuteWalkInspiratoryReserve(preDoc, postDoc) {
  const pre = pickSixMinuteWalkInspiratoryReserveSummary(preDoc);
  const post = pickSixMinuteWalkInspiratoryReserveSummary(postDoc);

  const percentPointChange =
    readFiniteNumber(pre.relativePercent) != null &&
    readFiniteNumber(post.relativePercent) != null
      ? post.relativePercent - pre.relativePercent
      : null;
  const deltaRatioChange =
    readFiniteNumber(pre.deltaRatio) != null &&
    readFiniteNumber(post.deltaRatio) != null
      ? post.deltaRatio - pre.deltaRatio
      : null;
  const peakHeightRatioChange =
    readFiniteNumber(pre.peakHeightRatio) != null &&
    readFiniteNumber(post.peakHeightRatio) != null &&
    Math.abs(pre.peakHeightRatio) > 1e-6
      ? post.peakHeightRatio / pre.peakHeightRatio
      : null;
  const relativeChangePercent =
    readFiniteNumber(peakHeightRatioChange) != null
      ? (peakHeightRatioChange - 1) * 100
      : null;

  return {
    pre,
    post,
    relativeChangePercent,
    absoluteDeltaPercent: percentPointChange,
    percentPointChange,
    deltaRatioChange,
    peakHeightRatioChange,
  };
}
